import { DEFAULT_LANGUAGE, SUPPORT_LANGUAGE } from './constant';
import dayjs from 'dayjs';

const CHINA_TIMEZONE_OFFSET_SECONDS = 8 * 60 * 60;

export const intersection = <T>(a: T[], b: T[]) => a.filter((v) => b.includes(v));

export function getChinaTimeUnix(value: dayjs.ConfigType): number {
  const date = dayjs(value);

  return (
    Date.UTC(date.year(), date.month(), date.date(), date.hour(), date.minute(), date.second(), date.millisecond()) /
      1000 -
    CHINA_TIMEZONE_OFFSET_SECONDS
  );
}

export function getChinaTimeValue(timestamp: number): dayjs.Dayjs {
  const chinaDate = new Date((timestamp + CHINA_TIMEZONE_OFFSET_SECONDS) * 1000);

  return dayjs(
    new Date(
      chinaDate.getUTCFullYear(),
      chinaDate.getUTCMonth(),
      chinaDate.getUTCDate(),
      chinaDate.getUTCHours(),
      chinaDate.getUTCMinutes(),
      chinaDate.getUTCSeconds(),
      chinaDate.getUTCMilliseconds(),
    ),
  );
}

export function getListTimeUnix(value: dayjs.ConfigType, timeType?: number): number {
  if (timeType === 0) {
    return getChinaTimeUnix(value);
  }

  return dayjs(value).valueOf() / 1000;
}

export function getListTimeValue(timestamp: number, timeType?: number): dayjs.Dayjs {
  if (timeType === 0) {
    return getChinaTimeValue(timestamp);
  }

  return dayjs(timestamp * 1000);
}

export const getUniqueFromSource = <T>(sourceArray: T[], excludeArray: T[]): T[] => {
  const excludeSet = new Set(excludeArray);
  return sourceArray.filter((item) => !excludeSet.has(item));
};

export function pathReplace<P>(url: string, path: P): string {
  if (!path) {
    return url;
  }
  let nextApi = url;
  Object.entries(path).forEach(([name, value]) => {
    const encodedValue = encodeURIComponent(value + '');
    nextApi = nextApi.replace(`{${name}}`, encodedValue);
  });
  return nextApi;
}

export function createFormData<D>(data: D): FormData | null {
  if (!data) {
    return null;
  }
  const formData = new FormData();
  Object.entries(data).forEach(([name, value]) => {
    formData.append(name, value as string);
  });
  return formData;
}

export function analyzeAcceptLanguage(
  acceptLanguageString: string,
  languageSpecies: string[] = SUPPORT_LANGUAGE,
): string {
  let result = DEFAULT_LANGUAGE;
  acceptLanguageString
    ?.split(',')
    ?.reverse()
    ?.forEach((_) => {
      languageSpecies.forEach((languageSpecie) => {
        if (_.includes(languageSpecie)) {
          result = languageSpecie;
        }
      });
    });

  return result;
}

export function hasEmptyFields<T>(array: T[], fields: Array<keyof T>) {
  return array.some((record) =>
    fields.some((field) => record[field] == null || (typeof record[field] === 'string' && record[field].trim() === '')),
  );
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function getMimeType(filename: string): string {
  const extension = filename?.split('?')?.[0].split('.').pop()?.toLowerCase();

  const mimeTypes: { [key: string]: string } = {
    // 图片类型
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',

    // 文档类型
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // 文本类型
    txt: 'text/plain',
    csv: 'text/csv',
    html: 'text/html',
    htm: 'text/html',
    css: 'text/css',
    js: 'application/javascript',
    ts: 'application/typescript',
    json: 'application/json',
    xml: 'application/xml',

    // 音频类型
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',

    // 视频类型
    mp4: 'video/mp4',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    webm: 'video/webm',

    // 压缩文件类型
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',

    // 其他类型
    exe: 'application/octet-stream',
    bin: 'application/octet-stream',
  };

  return mimeTypes[extension || ''] || 'application/octet-stream'; // 默认返回二进制流类型
}

export function stringToJson<T>(jsonString?: string): T | undefined {
  if (jsonString) {
    try {
      return JSON.parse(jsonString) as T;
    } catch (error) {
      console.error('Invalid JSON string:', error);
      return undefined;
    }
  }
  return undefined;
}

export function downloadBinaryData(data, filename) {
  // 创建 Blob 对象
  const blob = new Blob([data]);

  // 创建下载链接
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';

  // 触发点击下载
  document.body.appendChild(a);
  a.click();

  // 清理
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

export function toFixedNumber(value, digits = 2) {
  return Number(value.toFixed(digits));
}
